import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import BrandMark from "../../components/BrandMark";
import StatusChip from "../../components/StatusChip";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { CheckCircle2, XCircle, FileText } from "lucide-react";
import { API } from "../../lib/api";

export default function PublicProposal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [decision, setDecision] = useState(null);
  const [form, setForm] = useState({ signer_name: "", signer_email: "", consent: false });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.post(`${API}/proposals/public/view/${token}`);
      setData(r.data);
    } catch (e) {
      setErr("Proposal not found or link expired.");
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const decide = async (kind) => {
    if (!form.signer_name || !form.signer_email || !form.consent) {
      setErr("Name, email, and consent are required.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/proposals/public/decide/${token}?decision=${kind}`);
      setDecision(kind);
    } catch (e) {
      setErr(e.response?.data?.detail || "Unable to record decision.");
    } finally { setBusy(false); }
  };

  if (err && !data) {
    return <div className="min-h-screen bg-erf-dark text-erf-cream flex items-center justify-center p-6" data-testid="public-proposal-error">
      <div className="text-center"><BrandMark size={80} /><div className="mt-4 text-erf-cream/70">{err}</div></div>
    </div>;
  }
  if (!data) return <div className="min-h-screen bg-erf-cream flex items-center justify-center font-mono text-xs text-erf-dark/60">Loading proposal…</div>;

  const p = data.proposal, e = data.estimate;
  const total = p.price_summary?.total || e?.total || 0;
  const status = p.status;
  // Show terminal banner from decision (this session) OR from server status (reopened link).
  const effectiveDecision = decision || (status === "accepted" ? "accepted" : status === "rejected" ? "rejected" : null);
  const canDecide = effectiveDecision === null && ["sent", "viewed"].includes(status);

  return (
    <div className="min-h-screen bg-erf-cream text-erf-dark" data-testid="public-proposal-page">
      <header className="bg-erf-dark text-erf-cream px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3"><BrandMark size={48} /><div>
          <div className="font-display font-bold">ERF ENTERPRISES LLC</div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-erf-cream/50">Your Project. Our Promise.</div>
        </div></div>
        <StatusChip status={status === "accepted" ? "verified" : status === "rejected" ? "error" : "connecting"} label={status} />
      </header>

      <main className="max-w-3xl mx-auto p-6 space-y-6">
        <Card className="border-erf-borderCream">
          <CardContent className="p-6">
            <div className="text-[11px] font-mono uppercase tracking-widest text-erf-gold">Proposal</div>
            <h1 className="mt-2 font-display font-bold text-3xl">{p.scope_summary?.slice(0, 80) || "Construction Proposal"}</h1>
            {p.cover_letter && <p className="mt-4 text-sm font-nav whitespace-pre-wrap">{p.cover_letter}</p>}
            <div className="mt-6 border-t border-erf-borderCream pt-4">
              <div className="text-[11px] uppercase font-nav tracking-widest text-erf-dark/60">Scope Summary</div>
              <p className="mt-1 text-sm font-nav whitespace-pre-wrap">{p.scope_summary || "See attached scope."}</p>
            </div>
            <div className="mt-6 border-t border-erf-borderCream pt-4">
              <div className="text-[11px] uppercase font-nav tracking-widest text-erf-dark/60">Payment Schedule</div>
              <table className="w-full mt-2 text-sm">
                <tbody>
                  {(p.payment_schedule || []).map((s, i) => (
                    <tr key={i} className="border-t border-erf-borderCream/60">
                      <td className="py-1.5 font-nav">{s.label}</td>
                      <td className="py-1.5 font-mono text-right">{s.pct}%</td>
                      <td className="py-1.5 text-erf-dark/60 font-mono text-xs">{s.due_when}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 border-t border-erf-borderCream pt-4 flex items-baseline justify-between">
              <div className="text-[11px] uppercase font-nav tracking-widest text-erf-dark/60">Total</div>
              <div className="font-mono text-3xl font-bold">${Number(total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
            </div>
          </CardContent>
        </Card>

        {effectiveDecision === "accepted" && (
          <div className="border border-emerald-200 bg-emerald-50 text-emerald-900 rounded-md p-5 text-sm" data-testid="public-proposal-accepted">
            <CheckCircle2 className="w-6 h-6 inline-block mr-2 text-emerald-700" />
            <strong>Accepted.</strong> Thank you — ERF will contact you shortly to draft the contract.
          </div>
        )}
        {effectiveDecision === "rejected" && (
          <div className="border border-red-200 bg-red-50 text-red-900 rounded-md p-5 text-sm" data-testid="public-proposal-rejected">
            <XCircle className="w-6 h-6 inline-block mr-2" />
            <strong>Declined.</strong> Thank you for your consideration.
          </div>
        )}

        {canDecide && (
          <Card className="border-erf-gold/40 bg-erf-gold/5">
            <CardContent className="p-6 space-y-4">
              <div className="font-display font-bold text-lg">Your decision</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase font-nav">Full name</Label><Input required value={form.signer_name} onChange={(e)=>setForm({...form, signer_name: e.target.value})} className="mt-1 bg-white" data-testid="public-proposal-name" /></div>
                <div><Label className="text-xs uppercase font-nav">Email</Label><Input required type="email" value={form.signer_email} onChange={(e)=>setForm({...form, signer_email: e.target.value})} className="mt-1 bg-white font-mono" data-testid="public-proposal-email" /></div>
              </div>
              <label className="flex items-start gap-2 text-xs font-nav text-erf-dark/80">
                <input type="checkbox" checked={form.consent} onChange={(e)=>setForm({...form, consent: e.target.checked})} className="mt-0.5" data-testid="public-proposal-consent" />
                <span>I confirm I am authorised to accept or decline this proposal on behalf of the property owner. This is an acceptance of the proposal, not an e-signature on a legal contract.</span>
              </label>
              {err && <div className="text-xs text-red-700" data-testid="public-proposal-err">{err}</div>}
              <div className="flex gap-3">
                <Button onClick={() => decide("accepted")} disabled={busy} className="bg-emerald-700 text-white hover:bg-emerald-800" data-testid="public-proposal-accept">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Accept proposal
                </Button>
                <Button onClick={() => decide("rejected")} disabled={busy} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="public-proposal-reject">
                  <XCircle className="w-4 h-4 mr-2" /> Decline
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="text-[11px] font-mono text-erf-dark/40 uppercase tracking-widest text-center pt-6 pb-8">
          PA · NJ · DE · MD · DC · VA · NC · SC · GA · FL · Signed proof stored in ERF Audit Ledger
        </div>
      </main>
    </div>
  );
}
